import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { startAutonomousCreativeSession } from '../services/creativeSessionStart'; // I6, corrected 2026-09-02

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_COLORS = {
  'Psychology Map': 'text-purple-400 bg-purple-500/10',
  'Angle Selection': 'text-blue-400 bg-blue-500/10',
  'Hook Generation': 'text-amber-400 bg-amber-500/10',
  'Script Generation': 'text-emerald-400 bg-emerald-500/10',
  'Production': 'text-rose-400 bg-rose-500/10',
};

const SessionCard = ({ session, onClick, onViewDetail }) => {
  const stepClass = STEP_COLORS[session.step_label] || 'text-slate-400 bg-slate-500/10';
  const isActive = session.status === 'active';
  const isCompleted = session.status === 'completed';
  // Fix 4 — when the session is completed, show the completion timestamp
  // explicitly (falling back to updated_at when older sessions lack
  // completed_at; new sessions always have it via the /complete endpoint
  // and the auto-completion path in api_route_to_production).
  const timestampSource = isCompleted
    ? (session.completed_at || session.updated_at)
    : session.updated_at;
  const timestampLabel = isCompleted ? 'Completed' : 'Updated';
  const timestamp = timestampSource
    ? new Date(timestampSource).toLocaleString()
    : 'Unknown';

  return (
    <div
      className="w-full text-left p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600 hover:bg-slate-800 transition-all group"
      data-testid={`session-card-${session.session_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-white font-medium truncate">
            {session.offer_name || 'Unknown Offer'}
          </p>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${stepClass}`}>
              {session.step_label || 'Step 1'}
            </span>
            {isActive && (
              <span className="flex items-center gap-1 text-xs text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                Active
              </span>
            )}
            {isCompleted && (
              <span className="flex items-center gap-1 text-xs text-blue-400">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
                Completed
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
            <span>{session.operator_name || 'Unknown'}</span>
            <span data-testid={`session-${isCompleted ? 'completed-at' : 'updated-at'}`}>
              {timestampLabel}: {timestamp}
            </span>
            {session.message_count > 0 && (
              <span>{session.message_count} messages</span>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 flex-shrink-0 mt-1">
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetail(session); }}
            className="px-2.5 py-1 text-xs bg-slate-700/80 text-slate-300 rounded hover:bg-slate-600 transition-colors"
            data-testid={`session-detail-btn-${session.session_id}`}
          >
            Detail
          </button>
          <button
            onClick={() => onClick(session)}
            className="px-2.5 py-1 text-xs bg-emerald-500/15 text-emerald-400 rounded hover:bg-emerald-500/25 transition-colors"
            data-testid={`session-open-btn-${session.session_id}`}
          >
            Open
          </button>
        </div>
      </div>
    </div>
  );
};

// Session Detail Modal
// ============================================================================
// I5 — Creative Session Library detail view.
// Adds three panels the plain session detail didn't have: Related Sessions
// (other sessions for the same offer), Component Lineage (walks the full
// psychology-map -> angle -> hook -> script -> CTA -> creative-elements ->
// session -> performance chain via GET /creatives/{id}/lineage — the E3/
// BE-LINEAGE endpoint), and Performance (creative-engagement snapshots per
// produced creative). Lineage/performance are fetched lazily per-creative
// (on expand) rather than N+1'd on modal open.
// ============================================================================
const LineagePanel = ({ creativeId }) => {
  const [lineage, setLineage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (lineage || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/creatives/${creativeId}/lineage`);
      setLineage(res.data);
    } catch (err) {
      // E3/BE-LINEAGE is a concurrently-dispatched lane (see LANE_MANIFEST.md)
      // — degrade gracefully rather than breaking this panel if it's not
      // deployed yet, per this codebase's "failures return, do not throw" rule.
      setError(err.response?.data?.detail || 'Lineage not available for this creative yet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2">
      <button
        onClick={load}
        className="text-xs text-cyan-400 hover:text-cyan-300"
        data-testid={`lineage-toggle-${creativeId}`}
      >
        {open ? 'Hide lineage' : 'Show component lineage'}
      </button>
      {open && (
        <div className="mt-2 bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-xs space-y-1.5" data-testid={`lineage-panel-${creativeId}`}>
          {loading && <p className="text-slate-500">Loading lineage…</p>}
          {error && <p className="text-slate-500">{error}</p>}
          {lineage && (
            <>
              <p className="text-slate-400">
                <span className="text-slate-500">Angle:</span> {lineage.angle?.angle_name || '—'}
              </p>
              <p className="text-slate-400">
                <span className="text-slate-500">Hook:</span> {lineage.hook?.content ? `"${lineage.hook.content}"` : '—'}
              </p>
              <p className="text-slate-400">
                <span className="text-slate-500">CTA:</span> {lineage.cta?.content || '—'}
              </p>
              <p className="text-slate-400">
                <span className="text-slate-500">Originating session:</span>{' '}
                {lineage.originating_session?.production?.production_id
                  || lineage.originating_session?.ccs_session?.session_id
                  || lineage.originating_session?.note
                  || '—'}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CreativePerformancePanel = ({ creativeId }) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (rows || loading) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/performance/creative-engagement`, { params: { creative_id: creativeId, limit: 10 } });
      setRows(res.data?.snapshots || res.data?.data || []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-1.5">
      <button
        onClick={load}
        className="text-xs text-cyan-400 hover:text-cyan-300"
        data-testid={`performance-toggle-${creativeId}`}
      >
        {open ? 'Hide performance' : 'Show performance'}
      </button>
      {open && (
        <div className="mt-2 bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-xs" data-testid={`performance-panel-${creativeId}`}>
          {loading && <p className="text-slate-500">Loading performance…</p>}
          {rows && rows.length === 0 && <p className="text-slate-500">No performance snapshots yet — not launched, or spend hasn't posted.</p>}
          {rows && rows.length > 0 && (
            <div className="space-y-1">
              {rows.slice(0, 5).map((r, i) => (
                <p key={i} className="text-slate-400">
                  {r.date || r.snapshot_date || '—'}: CPM ${r.cpm ?? '—'}, CTR {r.ctr ?? '—'}%, spend ${r.spend ?? '—'}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SessionDetailModal = ({ session, allSessions = [], onClose, onOpenSession }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API}/creative/sessions/${session.session_id}/detail`);
        setDetail(res.data);
      } catch (err) {
        console.error('Failed to fetch session detail:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [session.session_id]);

  const DELIVERY_STATUS_COLORS = {
    SUCCESS: 'text-emerald-400 bg-emerald-500/10',
    FAILED: 'text-red-400 bg-red-500/10',
    RETRYING: 'text-amber-400 bg-amber-500/10',
    PENDING: 'text-slate-400 bg-slate-500/10',
  };

  // Related sessions — other sessions against the same offer (I5)
  const relatedSessions = (allSessions || []).filter(
    s => s.offer_id === session.offer_id && s.session_id !== session.session_id
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="session-detail-modal">
        <div className="bg-slate-900 rounded-xl border border-slate-700 p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="session-detail-modal">
      <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-semibold text-white" data-testid="session-detail-title">
              Session Detail
            </h3>
            <p className="text-slate-400 text-sm">{detail?.offer?.name || session.offer_name}</p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
            data-testid="session-detail-close-btn"
          >
            Close
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Session Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500 block text-xs mb-1">Session ID</span>
              <span className="text-white font-mono text-xs">{session.session_id}</span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs mb-1">Status</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                session.status === 'active' ? 'text-emerald-400 bg-emerald-500/10' : 'text-blue-400 bg-blue-500/10'
              }`}>
                {session.status}
              </span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs mb-1">Current Step</span>
              <span className="text-white text-sm">{session.step_label || 'Psychology Map'}</span>
            </div>
            <div>
              <span className="text-slate-500 block text-xs mb-1">Operator</span>
              <span className="text-white text-sm">{session.operator_name}</span>
            </div>
          </div>

          {/* Related Sessions (I5) — other sessions against this same offer */}
          {relatedSessions.length > 0 && (
            <div data-testid="related-sessions-section">
              <h4 className="text-sm font-medium text-white mb-2">Related Sessions ({relatedSessions.length})</h4>
              <div className="space-y-1.5">
                {relatedSessions.map(s => (
                  <button
                    key={s.session_id}
                    onClick={() => onOpenSession && onOpenSession(s)}
                    className="w-full flex items-center justify-between bg-slate-800/50 hover:bg-slate-800 rounded-lg px-3 py-2 text-left transition-colors"
                    data-testid={`related-session-${s.session_id}`}
                  >
                    <span className="text-white text-xs">Session {s.session_number ?? s.session_id?.slice(0, 8)}</span>
                    <span className="text-slate-500 text-xs">{s.step_label || 'Step 1'} &bull; {s.status}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected Angles */}
          {detail?.angles?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Selected Angles</h4>
              <div className="space-y-2">
                {detail.angles.map(a => (
                  <div key={a.angle_id} className="bg-slate-800/50 rounded-lg p-3 text-sm">
                    <p className="text-white font-medium">{a.angle_name}</p>
                    <p className="text-slate-400 text-xs mt-1">{a.core_belief_shift}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scripts */}
          {detail?.scripts?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Scripts ({detail.scripts.length})</h4>
              <div className="space-y-2">
                {detail.scripts.map(s => (
                  <div key={s.script_id} className="bg-slate-800/50 rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-emerald-400 font-mono text-xs">{s.body_element_number || s.script_id?.slice(0, 12)}</span>
                      <span className="text-white">{s.platform} &bull; {s.total_duration_seconds}s</span>
                      {s.status && (
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          s.status === 'APPROVED_FOR_PRODUCTION' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>{s.status?.replace(/_/g, ' ')}</span>
                      )}
                      {s.creative_type && (
                        <span className="text-xs text-slate-400">{s.creative_type}</span>
                      )}
                    </div>
                    {s.veo_prompt_optimized && (
                      <div className="mt-2 bg-slate-900/50 rounded p-2">
                        <p className="text-xs text-slate-500 mb-1">Veo Prompt</p>
                        <p className="text-xs text-slate-300 line-clamp-3">{s.veo_prompt_optimized}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Productions with Drive Links */}
          {detail?.productions?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Productions ({detail.productions.length})</h4>
              <div className="space-y-2">
                {detail.productions.map(p => (
                  <div key={p.production_id} className="bg-slate-800/50 rounded-lg p-3 text-sm">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                        p.creative_type === 'AI' ? 'bg-purple-500/20 text-purple-400' :
                        p.creative_type === 'VE' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-pink-500/20 text-pink-400'
                      }`}>{p.creative_type}</span>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        p.status === 'COMPLETED' ? 'bg-emerald-500/20 text-emerald-400' :
                        p.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>{p.status?.replace(/_/g, ' ')}</span>
                      {p.creative_identifier && (
                        <span className="text-xs text-indigo-300 font-mono">{p.creative_identifier}</span>
                      )}
                      {typeof p.pre_launch_score === 'number' && (
                        <span
                          data-testid={`pre-launch-score-${p.production_id || p.creative_id}`}
                          className={`px-2 py-0.5 text-xs rounded font-semibold ${
                            p.pre_launch_score >= 70
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                              : p.pre_launch_score >= 40
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                              : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                          }`}
                          title="Pre-launch PLCIE score"
                        >
                          PLCIE {p.pre_launch_score}/100
                        </span>
                      )}
                    </div>
                    {/* Drive Link */}
                    {(p.google_drive_url || p.drive_web_link) && (
                      <a
                        href={p.google_drive_url || p.drive_web_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 mt-2 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                        data-testid={`detail-drive-link-${p.production_id}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Open in Google Drive
                      </a>
                    )}
                    {p.drive_folder_path && (
                      <p className="text-xs text-slate-500 mt-1">Path: {p.drive_folder_path}</p>
                    )}
                    {/* Video info */}
                    {p.veo_video_size_mb && (
                      <p className="text-xs text-slate-500 mt-1">Video: {p.veo_video_size_mb} MB</p>
                    )}
                    {/* I5 — component lineage + performance, per produced creative */}
                    {p.creative_id && (
                      <>
                        <LineagePanel creativeId={p.creative_id} />
                        <CreativePerformancePanel creativeId={p.creative_id} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quality Feedback */}
          {detail?.creatives?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Quality Feedback</h4>
              <div className="space-y-2">
                {detail.creatives.filter(c => c.quality_rating_prelaunch || c.quality_rating_postrun).map(c => (
                  <div key={c.creative_id} className="bg-slate-800/50 rounded-lg p-3 text-sm">
                    <span className="text-indigo-300 font-mono text-xs">{c.creative_identifier || c.creative_id}</span>
                    <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
                      {c.quality_rating_prelaunch && (
                        <div>
                          <span className="text-slate-500">Pre-launch:</span>
                          <span className="text-white ml-1">{c.quality_rating_prelaunch}/5</span>
                          {c.quality_note_prelaunch && (
                            <p className="text-slate-400 mt-0.5">{c.quality_note_prelaunch}</p>
                          )}
                        </div>
                      )}
                      {c.quality_rating_postrun && (
                        <div>
                          <span className="text-slate-500">Post-run:</span>
                          <span className="text-white ml-1">{c.quality_rating_postrun}/5</span>
                          {c.quality_note_postrun && (
                            <p className="text-slate-400 mt-0.5">{c.quality_note_postrun}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivery Audit Log */}
          {detail?.delivery_audit?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-white mb-2">Delivery Audit</h4>
              <div className="space-y-1.5">
                {detail.delivery_audit.map(a => (
                  <div key={a.audit_id} className="flex items-center gap-2 text-xs bg-slate-800/30 rounded px-3 py-2" data-testid={`delivery-audit-${a.audit_id}`}>
                    <span className={`px-1.5 py-0.5 rounded font-medium ${DELIVERY_STATUS_COLORS[a.status] || 'text-slate-400 bg-slate-500/10'}`}>
                      {a.status}
                    </span>
                    <span className="text-white">{a.action}</span>
                    <span className="text-slate-500">{a.production_id}</span>
                    {a.error_message && (
                      <span className="text-red-400 truncate max-w-[200px]" title={a.error_message}>{a.error_message}</span>
                    )}
                    <span className="text-slate-600 ml-auto">{a.created_at ? new Date(a.created_at).toLocaleString() : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const CreativeHub = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [eligibleOffers, setEligibleOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [detailSession, setDetailSession] = useState(null);
  const [skQuery, setSkQuery] = useState("");
  const [skResults, setSkResults] = useState([]);
  const [skSearching, setSkSearching] = useState(false);
  const [skDecomposingId, setSkDecomposingId] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapVertical, setHeatmapVertical] = useState("");
  const [hoveredCell, setHoveredCell] = useState(null);

  const fetchHeatmap = useCallback(async (vertical = "") => {
    setHeatmapLoading(true);
    try {
      const params = vertical ? `?vertical=${encodeURIComponent(vertical)}` : "";
      const res = await axios.get(`${API}/swipekit/longevity-heatmap${params}`);
      setHeatmapData(res.data);
    } catch (e) {
      console.debug("Heatmap fetch failed:", e);
    } finally {
      setHeatmapLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchHeatmap();
  }, [fetchHeatmap]);

  const fetchData = async () => {
    try {
      const [sessRes, offRes] = await Promise.all([
        axios.get(`${API}/creative/sessions`),
        axios.get(`${API}/creative/eligible-offers`),
      ]);
      setSessions(sessRes.data.sessions || []);
      setEligibleOffers(offRes.data.offers || []);
    } catch (err) {
      console.error('Failed to load creative hub:', err);
    } finally {
      setLoading(false);
    }
  };

  // This Hub is the AUTONOMOUS half of the /creative chooser — `CreativeLanding`
  // in App.js renders it for `mode === "autonomous"` — so a session started
  // here must land in the guided workflow at /creative-engine/:offerId, never
  // in the Custom Creative Session.
  //
  // I6 read this component's lack of a REACT_APP_CCS_ENABLED check as drift and
  // routed it through the shared flag-branching start. With the flag on that
  // returned /creative?session=<id>, which CreativeLanding resolves back to
  // CustomCreativeSession — so clicking Autonomous Creative landed the operator
  // in the Custom session and made the guided workflow unreachable from the
  // chooser. The absent flag check was correct, not drift.
  //
  // `startAutonomousCreativeSession` is that branch made explicit: it never
  // consults the flag, and is identical to the shared start whenever the flag
  // is off. The flag itself is untouched — flipping it would break the Custom
  // card, which is the other half of the chooser.
  const handleCreateSession = async (offerId) => {
    setCreating(true);
    try {
      const res = await startAutonomousCreativeSession(offerId);
      if (!res.ok) {
        alert(res.error || 'Failed to create session');
        return;
      }
      // Fix 1 (preserved): that path already carries &new=1 so the engine
      // starts with a blank slate rather than falling through to the offer's
      // last-session data.
      navigate(res.path);
    } finally {
      setCreating(false);
      setShowNewSession(false);
    }
  };

  const handleSessionClick = (session) => {
    navigate(`/creative-engine/${session.offer_id}?session=${session.session_id}`);
  };

  const handleSwipekitSearch = async (e) => {
    e.preventDefault();
    if (!skQuery.trim()) return;
    setSkSearching(true);
    try {
      const res = await axios.get(`${API}/swipekit/search?query=${encodeURIComponent(skQuery)}&take=12&active_only=true`);
      setSkResults(res.data.ads || []);
    } catch (err) {
      console.error("Swipekit search failed:", err);
    } finally {
      setSkSearching(false);
    }
  };

  const handleSwipekitDecompose = async (swipekitId) => {
    setSkDecomposingId(swipekitId);
    try {
      // First pull and store the ad
      await axios.post(`${API}/swipekit/pull`, { query: skQuery, take: 20 });
      const res = await axios.post(`${API}/swipekit/decompose/${swipekitId}`);
      setSkResults(prev => prev.map(ad =>
        ad.swipekit_id === swipekitId ? { ...ad, decomposition: res.data.decomposition } : ad
      ));
    } catch (err) {
      console.error("Decompose failed:", err);
    } finally {
      setSkDecomposingId(null);
    }
  };

  const activeSessions = sessions.filter(s => s.status === 'active');
  const completedSessions = sessions.filter(s => s.status === 'completed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="creative-hub-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="creative-hub-title">Creative Hub</h2>
          <p className="text-slate-400 mt-1">Manage all creative engine sessions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.location.href = "/creative/image"}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors font-medium text-sm"
            data-testid="image-ad-studio-launcher"
            title="Gemini Nano Banana image ad generation"
          >
            Image Ad Studio
          </button>
          <button
            onClick={() => setShowNewSession(!showNewSession)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors font-medium text-sm"
            data-testid="start-new-session-btn"
          >
            + New Session
          </button>
        </div>
      </div>

      {/* New Session Panel */}
      {showNewSession && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="new-session-panel">
          <h3 className="text-lg font-semibold text-white mb-4">Start New Creative Session</h3>
          {eligibleOffers.length === 0 ? (
            <p className="text-slate-400 text-sm">
              No eligible offers. Offers must be at ACTIVATED status or beyond.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {eligibleOffers.map(offer => (
                <button
                  key={offer.offer_id}
                  onClick={() => handleCreateSession(offer.offer_id)}
                  disabled={creating}
                  className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg hover:border-emerald-500/50 hover:bg-slate-800 transition-all text-left disabled:opacity-50"
                  data-testid={`create-session-${offer.offer_id}`}
                >
                  <p className="text-white font-medium text-sm truncate">{offer.name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-slate-400">{offer.vertical || 'General'}</span>
                    <span className="text-xs text-emerald-400">{offer.status}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Swipekit Ad Search */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="swipekit-search-section">
        <h3 className="text-lg font-semibold text-white mb-1">Competitor Ad Search</h3>
        <p className="text-slate-400 text-sm mb-4">Search Swipekit for competitor ad creatives by keyword, vertical, or advertiser</p>
        <form onSubmit={handleSwipekitSearch} className="flex gap-2">
          <input
            type="text"
            value={skQuery}
            onChange={(e) => setSkQuery(e.target.value)}
            placeholder="e.g. weight loss, keto supplements, anti aging..."
            className="flex-1 px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
            data-testid="swipekit-search-input"
          />
          <button
            type="submit"
            disabled={skSearching || !skQuery.trim()}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors text-sm disabled:opacity-40"
            data-testid="swipekit-search-btn"
          >
            {skSearching ? "Searching..." : "Search"}
          </button>
        </form>

        {skResults.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="swipekit-results-grid">
            {skResults.map((ad) => (
              <div key={ad.swipekit_id} className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 space-y-2">
                {ad.thumbnail_url && (
                  <img
                    src={ad.thumbnail_url}
                    alt=""
                    className="w-full h-28 rounded-lg object-cover bg-slate-700"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white font-medium truncate">{ad.advertiser || "Unknown"}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-300">{ad.platform}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span>{ad.active_days || 0}d active</span>
                  <span>{ad.asset_type}</span>
                  {ad.status === "ACTIVE" && <span className="text-emerald-400">Live</span>}
                </div>
                {ad.ad_copy && (
                  <p className="text-xs text-slate-400 line-clamp-2">{ad.ad_copy.slice(0, 120)}</p>
                )}
                {ad.decomposition ? (
                  <div className="bg-slate-900/50 rounded p-2">
                    <p className="text-xs text-emerald-400 font-medium mb-1">Decomposed</p>
                    <p className="text-xs text-slate-300 italic">{ad.decomposition.summary}</p>
                  </div>
                ) : (
                  <button
                    onClick={() => handleSwipekitDecompose(ad.swipekit_id)}
                    disabled={skDecomposingId === ad.swipekit_id}
                    className="w-full px-2 py-1 text-xs bg-slate-700/50 text-slate-300 rounded hover:bg-slate-700 transition-colors disabled:opacity-50"
                    data-testid={`sk-decompose-${ad.swipekit_id}`}
                  >
                    {skDecomposingId === ad.swipekit_id ? "Analyzing..." : "Decompose"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Ad Longevity Heatmap */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="longevity-heatmap-section">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Ad Longevity Heatmap</h3>
            <p className="text-slate-400 text-sm mt-0.5">
              Format × Hook → avg active days ({heatmapData?.total_ads || 0} ads analyzed)
            </p>
          </div>
          <select
            value={heatmapVertical}
            onChange={(e) => { setHeatmapVertical(e.target.value); fetchHeatmap(e.target.value); }}
            className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:border-emerald-500 focus:outline-none w-full sm:w-44"
            data-testid="heatmap-vertical-filter"
          >
            <option value="">All Verticals</option>
            <option value="Health">Health</option>
            <option value="Fitness">Fitness</option>
            <option value="Beauty">Beauty</option>
            <option value="Finance">Finance</option>
            <option value="Technology">Technology</option>
            <option value="Education">Education</option>
          </select>
        </div>

        {heatmapLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : !heatmapData || heatmapData.cells?.length === 0 ? (
          <p className="text-slate-500 text-center py-8 text-sm">No decomposed ad data yet. Search and decompose ads above to populate the heatmap.</p>
        ) : (
          <div className="space-y-4">
            {/* Heatmap Grid */}
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full">
                {/* Hook headers row */}
                <div className="flex items-end gap-1 mb-1 ml-24">
                  {(heatmapData.hooks || []).map((hook) => (
                    <div key={hook} className="w-20 flex-shrink-0 text-center">
                      <span className="text-xs text-slate-500 leading-tight block" style={{writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: '64px'}}>
                        {hook}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Format rows */}
                {(heatmapData.formats || []).map((format) => (
                  <div key={format} className="flex items-center gap-1 mb-1">
                    <div className="w-24 flex-shrink-0 text-right pr-2">
                      <span className="text-xs text-slate-400 font-medium capitalize">{format}</span>
                    </div>
                    {(heatmapData.hooks || []).map((hook) => {
                      const cell = (heatmapData.cells || []).find(c => c.format === format && c.hook === hook);
                      if (!cell) {
                        return (
                          <div key={hook} className="w-20 h-14 flex-shrink-0 rounded bg-slate-800/30 border border-slate-800/50" />
                        );
                      }
                      const intensity = cell.intensity;
                      // Green gradient: higher intensity = brighter green
                      const r = Math.round(16 + (1 - intensity) * 80);
                      const g = Math.round(80 + intensity * 175);
                      const b = Math.round(30 + (1 - intensity) * 40);
                      const bg = `rgb(${r}, ${g}, ${b})`;

                      return (
                        <div
                          key={hook}
                          className="w-20 h-14 flex-shrink-0 rounded cursor-pointer transition-all hover:scale-105 hover:z-10 relative flex flex-col items-center justify-center"
                          style={{ backgroundColor: bg, opacity: 0.2 + intensity * 0.8 }}
                          onMouseEnter={() => setHoveredCell(cell)}
                          onMouseLeave={() => setHoveredCell(null)}
                          data-testid={`heatmap-cell-${format}-${hook}`}
                        >
                          <span className="text-sm font-bold text-white drop-shadow">{cell.avg_days}d</span>
                          <span className="text-xs text-white/70">{cell.count} ads</span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Hover Tooltip */}
            {hoveredCell && (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm" data-testid="heatmap-tooltip">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white font-medium capitalize">{hoveredCell.format}</span>
                  <span className="text-slate-500">×</span>
                  <span className="text-emerald-400 font-medium">{hoveredCell.hook}</span>
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                  <div className="text-slate-400">Avg Active Days</div>
                  <div className="text-white font-medium">{hoveredCell.avg_days}d</div>
                  <div className="text-slate-400">Min / Max</div>
                  <div className="text-white">{hoveredCell.min_days}d — {hoveredCell.max_days}d</div>
                  <div className="text-slate-400">Ad Count</div>
                  <div className="text-white">{hoveredCell.count}</div>
                  <div className="text-slate-400">Top Emotion</div>
                  <div className="text-white">{hoveredCell.top_emotion || "—"}</div>
                  <div className="text-slate-400">Sample Brand</div>
                  <div className="text-white">{hoveredCell.sample_advertiser || "—"}</div>
                </div>
              </div>
            )}

            {/* Top & Bottom Performers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-slate-800/50">
              <div>
                <h4 className="text-xs text-emerald-400 font-medium mb-2">Longest-Lived Combos</h4>
                {(heatmapData.top_performers || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-slate-300 capitalize">{c.format} × {c.hook}</span>
                    <span className="text-white font-medium">{c.avg_days}d <span className="text-slate-500">({c.count})</span></span>
                  </div>
                ))}
              </div>
              <div>
                <h4 className="text-xs text-red-400 font-medium mb-2">Shortest-Lived Combos</h4>
                {(heatmapData.bottom_performers || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs">
                    <span className="text-slate-300 capitalize">{c.format} × {c.hook}</span>
                    <span className="text-white font-medium">{c.avg_days}d <span className="text-slate-500">({c.count})</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active Sessions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">Active Sessions</h3>
            <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
              {activeSessions.length}
            </span>
          </div>
          <button
            onClick={() => setShowNewSession(!showNewSession)}
            className="px-3 py-1.5 text-sm bg-emerald-600/20 text-emerald-400 rounded-lg hover:bg-emerald-500/30 transition-colors border border-emerald-600/30"
            data-testid="new-session-active-btn"
          >
            + New Session
          </button>
        </div>
        {activeSessions.length === 0 ? (
          <div className="text-center py-8 bg-slate-900/30 rounded-xl border border-slate-800">
            <p className="text-slate-500 text-sm">No active sessions</p>
            <p className="text-slate-600 text-xs mt-1">Start a new session to begin creative work</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {activeSessions.map(session => (
              <SessionCard
                key={session.session_id}
                session={session}
                onClick={handleSessionClick}
                onViewDetail={(s) => setDetailSession(s)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Completed Sessions */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold text-white">Completed Sessions</h3>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
            {completedSessions.length}
          </span>
        </div>
        {completedSessions.length === 0 ? (
          <div className="text-center py-8 bg-slate-900/30 rounded-xl border border-slate-800">
            <p className="text-slate-500 text-sm">No completed sessions yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {completedSessions.map(session => (
              <SessionCard
                key={session.session_id}
                session={session}
                onClick={handleSessionClick}
                onViewDetail={(s) => setDetailSession(s)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Session Detail Modal */}
      {detailSession && (
        <SessionDetailModal
          session={detailSession}
          allSessions={sessions}
          onOpenSession={(s) => setDetailSession(s)}
          onClose={() => setDetailSession(null)}
        />
      )}
    </div>
  );
};

export default CreativeHub;
